import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { 
  Crown, 
  Filter, 
  Shield, 
  MessageSquare, 
  Building, 
  TrendingUp, 
  Heart, 
  Sliders,
  Check,
  AlertCircle,
  Users
} from 'lucide-react';

interface AdminMonetizationPanelProps {
  userId?: string; // Optional - if provided, shows settings for a specific user
}

export function AdminMonetizationPanel({ userId }: AdminMonetizationPanelProps) {
  const [loading, setLoading] = useState(true);
  const [adminId, setAdminId] = useState<string | null>(null);
  const [features, setFeatures] = useState({
    pro_matching: false,
    verified_only_filter: false,
    dealbreaker_cap: false,
    concierge_support: false,
    premium_listings: false,
    paid_placement: false,
    boosted_stories: false,
    premium_variables: false
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadAdminSettings();
  }, [userId]);

  const loadAdminSettings = async () => {
    try {
      setLoading(true);
      
      // Get current user if userId not provided
      const targetId = userId || (await supabase.auth.getUser()).data.user?.id;
      setAdminId(targetId || null);
      
      if (!targetId) {
        throw new Error('No user ID available');
      }

      // Get monetization settings
      const { data, error } = await supabase
        .from('profiles')
        .select('monetization_settings')
        .eq('id', targetId)
        .single();

      if (error) throw error;
      
      // Update feature states
      const settings = data?.monetization_settings || {};
      setFeatures({
        pro_matching: settings.pro_matching === true,
        verified_only_filter: settings.verified_only_filter === true,
        dealbreaker_cap: settings.dealbreaker_cap === true,
        concierge_support: settings.concierge_support === true,
        premium_listings: settings.premium_listings === true,
        paid_placement: settings.paid_placement === true,
        boosted_stories: settings.boosted_stories === true,
        premium_variables: settings.premium_variables === true
      });
    } catch (err) {
      console.error('Error loading admin settings:', err);
      setError('Failed to load monetization settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleFeature = async (feature: string) => {
    if (!adminId) return;
    
    try {
      setError(null);
      setSuccess(null);
      
      // Get current settings
      const { data } = await supabase
        .from('profiles')
        .select('monetization_settings')
        .eq('id', adminId)
        .single();
      
      const currentSettings = data?.monetization_settings || {};
      const newValue = !(currentSettings[feature] === true);
      
      // Update settings
      const updatedSettings = {
        ...currentSettings,
        [feature]: newValue
      };
      
      const { error } = await supabase
        .from('profiles')
        .update({ monetization_settings: updatedSettings })
        .eq('id', adminId);
      
      if (error) throw error;
      
      // Log the event
      await supabase
        .from('monetization_events')
        .insert({
          user_id: adminId,
          feature,
          action: newValue ? 'enabled' : 'disabled',
          previous_state: !newValue,
          new_state: newValue
        });
      
      // Update local state
      setFeatures(prev => ({
        ...prev,
        [feature]: newValue
      }));
      
      setSuccess(`${feature.replace(/_/g, ' ')} ${newValue ? 'enabled' : 'disabled'} successfully`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error(`Error toggling ${feature}:`, err);
      setError(err instanceof Error ? err.message : 'Failed to update feature');
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 bg-gray-200 rounded w-3/4"></div>
        <div className="h-40 bg-gray-200 rounded w-full"></div>
      </div>
    );
  }

  if (!adminId) {
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded-lg">
        No user ID available. Please log in as an admin.
      </div>
    );
  }

  const featureConfig = [
    {
      id: 'pro_matching',
      name: 'Pro Matching',
      description: 'Advanced algorithm for better roommate matches',
      icon: <Crown className="w-5 h-5 text-yellow-500" />
    },
    {
      id: 'verified_only_filter',
      name: 'Verified Only Filter',
      description: 'Filter to only see verified profiles',
      icon: <Filter className="w-5 h-5 text-blue-500" />
    },
    {
      id: 'dealbreaker_cap',
      name: 'Unlimited Dealbreakers',
      description: 'Remove the cap on absolute dealbreakers',
      icon: <Shield className="w-5 h-5 text-red-500" />
    },
    {
      id: 'concierge_support',
      name: 'Concierge Support',
      description: 'Priority customer support',
      icon: <MessageSquare className="w-5 h-5 text-green-500" />
    },
    {
      id: 'premium_listings',
      name: 'Premium Listings',
      description: 'Access to exclusive property listings',
      icon: <Building className="w-5 h-5 text-purple-500" />
    },
    {
      id: 'paid_placement',
      name: 'Paid Placement',
      description: 'Higher visibility in search results',
      icon: <TrendingUp className="w-5 h-5 text-orange-500" />
    },
    {
      id: 'boosted_stories',
      name: 'Boosted Stories',
      description: 'Highlight user stories in the community',
      icon: <Heart className="w-5 h-5 text-pink-500" />
    },
    {
      id: 'premium_variables',
      name: 'Premium Variables',
      description: 'Access to 12 additional preference variables',
      icon: <Sliders className="w-5 h-5 text-indigo-500" />
    }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center gap-3 mb-6">
        <Crown className="w-6 h-6 text-yellow-500" />
        <div>
          <h2 className="text-xl font-semibold">Admin Monetization Features</h2>
          <p className="text-gray-600">Toggle premium features with a single click</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          <span>{success}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {featureConfig.map(feature => (
          <div 
            key={feature.id}
            className={`border rounded-lg p-4 flex items-center justify-between transition-colors ${
              features[feature.id as keyof typeof features] 
                ? 'border-primary-400 bg-primary-50' 
                : 'border-gray-200'
            }`}
          >
            <div className="flex items-center gap-3">
              {feature.icon}
              <div>
                <h3 className="font-medium">{feature.name}</h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </div>
            </div>
            
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={features[feature.id as keyof typeof features]}
                onChange={() => toggleFeature(feature.id)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}